import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
    Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
    Divider, Typography, TextField, LinearProgress, Breadcrumbs, 
    Link, Stack, FormHelperText, 
    Grid
} from '@mui/material';
import { Home, InsertPhoto } from '@mui/icons-material';
import ImageUploader from '../../common/ImageUploader';
import AnimateButton from '../../../AnimateButton';
import { BUTTON_CANCEL_COLOR, BUTTON_SAVE_COLOR } from '../../../common/color';
import Swal from 'sweetalert2';
import { getImageUrl } from '../../../../services/api';
import partnerApi from '../../../../services/partnerApi';

const PartnerBody = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const isNew = id === 'new';
    const [formData, setFormData] = useState({
        name: '',
        websiteUrl: '',
        image: null
    });
    const [previewUrl, setPreviewUrl] = useState('');
    const [existingImageUrl, setExistingImageUrl] = useState('');
    const [errors, setErrors] = useState({ name: '', image: '' });
    const [touched, setTouched] = useState({ name: false, image: false });
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (!isNew) {
            const fetchPartner = async () => {
                try {
                    const partner = await partnerApi.getById(id);
                    setFormData({
                        name: partner.name || '',
                        websiteUrl: partner.websiteUrl || ''
                    });
    
                    if (partner.logoUrl) {
                        const fullUrl = getImageUrl(partner.logoUrl);
                        setPreviewUrl(fullUrl);
                        setExistingImageUrl(fullUrl);
                    }
                } catch (error) {
                    console.error('Fetch error:', error);
                    Swal.fire('Error', 'Failed to load partner', 'error');
                    navigate('/admin/partners');
                } finally {
                    setLoading(false);
                }
            };
            fetchPartner();
        } else {
            setLoading(false);
        }
    }, [id, isNew, navigate]);

    useEffect(() => {
        return () => {
            if (previewUrl?.startsWith('blob:')) {
                URL.revokeObjectURL(previewUrl);
            }
        };
    }, [previewUrl]);

    const validateField = (name, value) => {
        switch (name) {
            case 'name': return value.trim() ? '' : 'Name is required';
            case 'image': 
                return (isNew ? !previewUrl : (!previewUrl && !existingImageUrl)) 
                    ? 'Image is required' 
                    : '';
            default: return '';
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (touched[name]) {
            setErrors(prev => ({ ...prev, [name]: validateField(name, value) }));
        }
    };

    const handleBlur = (e) => {
        const { name } = e.target;
        setTouched(prev => ({ ...prev, [name]: true }));
        setErrors(prev => ({ ...prev, [name]: validateField(name, formData[name]) }));
    };

    const handleImageSelect = (file) => {
        if (!file) return;
        
        if (previewUrl?.startsWith('blob:')) {
            URL.revokeObjectURL(previewUrl);
        }

        const objectUrl = URL.createObjectURL(file);
        setFormData(prev => ({ ...prev, image: file }));
        setPreviewUrl(objectUrl);
        setErrors(prev => ({ ...prev, image: '' }));
    };


    const handleSubmit = async () => {
        setTouched({ name: true, image: true });
        
        const newErrors = {
            name: validateField('name', formData.name),
            image: validateField('image', formData.image)
        };
        
        setErrors(newErrors);
        
        if (newErrors.name || newErrors.image) {
            return;
        }
      
        const confirm = await Swal.fire({
            title: 'Confirm',
            text: `Are you sure you want to ${isNew ? 'create' : 'update'} this partner?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: BUTTON_SAVE_COLOR,
            cancelButtonColor: BUTTON_CANCEL_COLOR
        });
      
        if (!confirm.isConfirmed) return;
      
        setIsSubmitting(true);
        try {
            const formDataToSend = new FormData();
            formDataToSend.append('name', formData.name);
            formDataToSend.append('websiteUrl', formData.websiteUrl);
            
            if (formData.image) {
                formDataToSend.append('image', formData.image);
            }
      
            if (isNew) {
                await partnerApi.create(formDataToSend);
            } else {
                await partnerApi.update(id, formDataToSend);
            }
      
            Swal.fire('Success', `Partner ${isNew ? 'created' : 'updated'}`, 'success');
            navigate('/admin/partners');
        } catch (error) {
            console.error('Error:', error);
            Swal.fire('Error', error.response?.data?.error || 'Operation failed', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading) return <LinearProgress />;

    return (
        <Box sx={{ width: '100%', p: 3 }}>
            <Dialog open fullWidth maxWidth="md" onClose={() => navigate('/admin/partners')}>
                <DialogTitle>
                    <Stack direction="row" justifyContent="space-between">
                        <Breadcrumbs>
                            <Link href="#" underline="hover" color="inherit">
                                <Home sx={{ mr: 0.5 }} /> Master Management
                            </Link>
                            <Link href="/admin/partners" underline="hover" color="inherit">
                                Partners
                            </Link>
                            <Typography color="text.primary">
                                {isNew ? 'Create' : 'Edit'}
                            </Typography>
                        </Breadcrumbs>
                    </Stack>
                </DialogTitle>
                
                <Divider />
                
                <DialogContent sx={{ p: 3 }}>
                    <Grid container spacing={3}>
                        {/* Left Column - Form Fields and File Upload */}
                        <Grid item xs={12} md={6}>
                            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                                Partner Details
                            </Typography>
                            
                            <TextField
                                required
                                fullWidth
                                label="Partner Name"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                onBlur={handleBlur}
                                error={touched.name && Boolean(errors.name)}
                                helperText={touched.name && errors.name}
                                sx={{ mb: 3 }}
                                size="small"
                            />
                            <TextField
                                required
                                fullWidth
                                label="Website URL"
                                name="websiteUrl"
                                value={formData.websiteUrl}
                                onChange={handleInputChange}
                                onBlur={handleBlur}
                                error={touched.websiteUrl && Boolean(errors.websiteUrl)}
                                helperText={touched.websiteUrl && errors.websiteUrl}
                                sx={{ mb: 3 }}
                                size="small"
                            />
                            
                            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 3 }}>
                                Upload Image
                            </Typography>
                            
                            <Box sx={{ 
                                p: 2,
                                border: '1px dashed',
                                borderColor: errors.image && touched.image ? 'error.main' : 'grey.300',
                                borderRadius: 1,
                                backgroundColor: 'grey.50',
                                textAlign: 'center'
                            }}>
                                <ImageUploader 
                                    onFileSelect={handleImageSelect}
                                    accept="image/*"
                                    maxSize={5 * 1024 * 1024}
                                />
                                <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 1 }}>
                                    Supported formats: JPEG, PNG, GIF
                                </Typography>
                                <Typography variant="caption" color="textSecondary" display="block">
                                    Maximum size: 5MB
                                </Typography>
                                {errors.image && touched.image && (
                                    <FormHelperText error sx={{ mt: 1 }}>{errors.image}</FormHelperText>
                                )}
                            </Box>
                        </Grid>

                        {/* Right Column - Image Preview */}
                        <Grid item xs={12} md={6}>
                            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                                Image Preview
                            </Typography>
                            
                            <Box sx={{ 
                                p: 2,
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1,
                                height: '100%',
                                minHeight: 300,
                                display: 'flex',
                                flexDirection: 'column'
                            }}>
                                {previewUrl || existingImageUrl ? (
                                    <>
                                        <Box sx={{ 
                                            flexGrow: 1,
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            position: 'relative'
                                        }}>
                                            <img
                                                src={previewUrl || existingImageUrl}
                                                alt="Partner Preview"
                                                style={{
                                                    maxWidth: '100%',
                                                    maxHeight: 250,
                                                    objectFit: 'contain',
                                                    borderRadius: 4
                                                }}
                                            />
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    right: 0,
                                                    bottom: 0,
                                                    backgroundColor: 'rgba(0,0,0,0.5)',
                                                    opacity: 0,
                                                    transition: 'opacity 0.3s',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    borderRadius: 1,
                                                    cursor: 'pointer',
                                                    '&:hover': { opacity: 1 }
                                                }}
                                                onClick={() => window.open(previewUrl || existingImageUrl, '_blank')}
                                            >
                                                <Typography variant="caption" color="white">
                                                    Click to view full size
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </>
                                ) : (
                                    <Box sx={{ 
                                        flexGrow: 1,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        color: 'text.secondary'
                                    }}>
                                        <InsertPhoto sx={{ fontSize: 48, mb: 1 }} />
                                        <Typography>No image selected</Typography>
                                        <Typography variant="caption" sx={{ mt: 1 }}>
                                            Upload an image to see preview
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </Grid>
                    </Grid>
                </DialogContent>
                
                <Divider />
                
                <DialogActions sx={{ p: 3 }}>
                    <AnimateButton>
                        <Button
                            variant="contained"
                            onClick={() => navigate('/admin/partners')}
                            sx={{ background: BUTTON_CANCEL_COLOR }}
                        >
                            Cancel
                        </Button>
                    </AnimateButton>
                    <AnimateButton>
                        <Button
                            variant="contained"
                            onClick={handleSubmit}
                            disabled={isSubmitting}
                            sx={{ background: BUTTON_SAVE_COLOR }}
                        >
                            {isSubmitting ? 'Saving...' : isNew ? 'Create' : 'Save'}
                        </Button>
                    </AnimateButton>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default PartnerBody;